import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Button from "../components/base/Button";
import FormField from "../components/FormField";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Logo } from "../components/App";
import Icons from "../components/Icons";
import { passwordSchema } from "../shared/utils/formikValidators";
import { errorMessage } from "../shared/utils/validators";
import { Alert, Snackbar } from "@mui/material";
import apiConfig from "../config/api.config";
import appConfig from "../shared/js/app-config";
import { OAuthProviderButtons } from "./OAuthProviderButtons";
import MyAccountService from "../services/MyAccountService";
import ServicesService from "../services/ServicesService";
import AuthService from "../services/AuthService";
import { persistAuthTokenFromResponse } from "../shared/authToken";
import "./authPages.css";
import "./signin.css";

const LOGIN_MODE = {
  Password: "password",
  Otp: "otp",
};

const OTP_STEP = {
  Request: "request",
  Verify: "verify",
};

const loginIdSchema = Yup.string()
  .trim()
  .required("Required mobile or email")
  .test(
    "mobile-or-email",
    "Enter a valid email address or 10 digit mobile number",
    (value) => {
      if (!value) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[0-9]{10}$/;
      return emailRegex.test(value) || mobileRegex.test(value);
    },
  );

function SignInTabs({ loginMode, onModeChange }) {
  return (
    <div className="signin-tabs">
      <button
        type="button"
        className={loginMode === LOGIN_MODE.Password ? "active" : ""}
        onClick={() => onModeChange(LOGIN_MODE.Password)}
      >
        Password
      </button>
      <button
        type="button"
        className={loginMode === LOGIN_MODE.Otp ? "active" : ""}
        onClick={() => onModeChange(LOGIN_MODE.Otp)}
      >
        OTP
      </button>
    </div>
  );
}

function OtpInputs({ value, onChange }) {
  const digits = (value + "    ").slice(0, 4).split("");

  function updateDigit(index, digit) {
    const code = [...digits];
    code[index] = digit;
    onChange(code.join("").replace(/\s/g, ""));
  }

  return (
    <div className="auth-otp-boxes">
      {Array.from({ length: 4 }).map((_, idx) => (
        <input
          key={idx}
          type="text"
          inputMode="numeric"
          maxLength="1"
          value={digits[idx]?.trim() || ""}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, "");
            if (!val) {
              updateDigit(idx, "");
              return;
            }
            updateDigit(idx, val.slice(-1));
            if (e.target.nextSibling) {
              e.target.nextSibling.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" || e.key === "Delete") {
              if (digits[idx]?.trim()) {
                e.preventDefault();
                updateDigit(idx, "");
                return;
              }
              if (e.key === "Backspace" && e.target.previousSibling) {
                e.preventDefault();
                updateDigit(idx - 1, "");
                e.target.previousSibling.focus();
              }
            }
          }}
        />
      ))}
    </div>
  );
}

function SignInPage() {
  const [visible, setVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loginMode, setLoginMode] = useState(LOGIN_MODE.Password);
  const [otpStep, setOtpStep] = useState(OTP_STEP.Request);
  const [verificationId, setVerificationId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const domain = apiConfig.BASE_URL;
  const signInUrl = `${domain}/signin`;
  const $userName = useRef();
  const $otpUserName = useRef();

  function showError(message) {
    setSnackbar({
      open: true,
      message: message || "Something went wrong",
      severity: "error",
    });
  }

  async function redirectAfterLogin() {
    await appConfig.fetch();
    const account = await MyAccountService.fetch();
    const organizationId = account.getOrganizationId();
    if (organizationId == null) {
      if (window.location.pathname !== "/create-service") {
        window.location.replace("/create-service");
      }
      return;
    }
    const service = await ServicesService.getOrganizationServices({
      organizationId,
    });
    if (service.length === 0) {
      if (window.location.pathname !== "/create-service") {
        window.location.replace("/create-service");
      }
      return;
    }
    window.location.replace("/");
  }

  async function handlePasswordSubmit(values) {
    setFormSubmitted(true);
    try {
      const { data } = await axios.post(
        signInUrl,
        new URLSearchParams({
          username: values.userName.trim(),
          password: values.password.trim(),
          type: "user",
        }),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          withCredentials: true,
        },
      );
      persistAuthTokenFromResponse(data);
      await redirectAfterLogin();
    } catch (error) {
      showError(errorMessage(error));
    }
  }

  async function handleOtpSubmit(values) {
    setFormSubmitted(true);
    try {
      if (otpStep === OTP_STEP.Request) {
        const id = await AuthService.sendLoginOtp(values.userName);
        if (id?.mobileVerificationId) {
          setVerificationId(id.mobileVerificationId);
        } else if (id?.emailVerificationId) {
          setVerificationId(id.emailVerificationId);
        } else if (typeof id === "string") {
          setVerificationId(id);
        }
        setOtpStep(OTP_STEP.Verify);
        return;
      }

      if (!values.verificationCode || values.verificationCode.trim().length < 4) {
        showError("Enter the OTP");
        return;
      }

      const otpResult = await AuthService.signInWithOtp({
        loginId: values.userName,
        verificationId,
        verificationCode: values.verificationCode.trim(),
      });
      persistAuthTokenFromResponse(otpResult);
      await redirectAfterLogin();
    } catch (error) {
      showError(error?.message || errorMessage(error));
    }
  }

  const passwordFormik = useFormik({
    initialValues: { userName: "", password: "" },
    validationSchema: Yup.object().shape({
      userName: loginIdSchema,
      password: passwordSchema,
    }),
    onSubmit: handlePasswordSubmit,
  });

  const otpFormik = useFormik({
    initialValues: { userName: "", verificationCode: "" },
    validationSchema: Yup.object().shape({
      userName: loginIdSchema,
      verificationCode: Yup.string(),
    }),
    onSubmit: handleOtpSubmit,
  });

  function handleModeChange(mode) {
    setLoginMode(mode);
    setOtpStep(OTP_STEP.Request);
    setVerificationId(null);
    setFormSubmitted(false);
    otpFormik.setFieldValue("verificationCode", "");
  }

  function resetOtpFlow() {
    setOtpStep(OTP_STEP.Request);
    setVerificationId(null);
    otpFormik.setFieldValue("verificationCode", "");
  }

  useEffect(() => {
    document.title = "Sign in · WappGrow";
  }, []);

  useEffect(() => {
    if (loginMode === LOGIN_MODE.Password) {
      $userName.current?.focus?.();
    } else {
      $otpUserName.current?.focus?.();
    }
  }, [loginMode, otpStep]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get("oauth_error");
    if (oauthErr) {
      showError(
        "Google or Facebook sign-in did not complete. Configure OAuth on the server, or sign in with your number and password.",
      );
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  function snackBar() {
    return (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    );
  }

  function renderPasswordForm() {
    return (
      <form onSubmit={passwordFormik.handleSubmit} className="auth-form">
        <div className="auth-field-wrap auth-focus-input">
          <FormField
            ref={$userName}
            type="auth-text"
            name="userName"
            placeholder="Email or mobile"
            value={passwordFormik.values.userName}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            error={
              (formSubmitted || passwordFormik.submitCount > 0)
              && passwordFormik.touched.userName
              && passwordFormik.errors.userName
            }
          />
        </div>
        <div className="auth-field-wrap auth-focus-input">
          <FormField
            autoComplete="off"
            name="password"
            placeholder="Password"
            value={passwordFormik.values.password}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            error={
              (formSubmitted || passwordFormik.submitCount > 0)
              && passwordFormik.touched.password
              && passwordFormik.errors.password
            }
            type={visible ? "text" : "password"}
            iconSuffix={
              passwordFormik.values.password && (
                <div
                  style={{ cursor: "pointer", display: "flex" }}
                  onClick={() => setVisible(!visible)}
                  aria-label={visible ? "Hide password" : "Show password"}
                >
                  {visible ? Icons.Visible : Icons.VisibleOff}
                </div>
              )
            }
            maxLength={25}
          />
        </div>
        <div className="auth-forgot-row">
          <a href="/forgot-password">Forgot password?</a>
        </div>
        <Button type="submit" appearance="auth-btn" className="auth-primary-btn">
          Sign in
        </Button>
      </form>
    );
  }

  function renderOtpForm() {
    if (otpStep === OTP_STEP.Request) {
      return (
        <form onSubmit={otpFormik.handleSubmit} className="auth-form">
          <div className="auth-field-wrap auth-focus-input">
            <FormField
              ref={$otpUserName}
              type="auth-text"
              name="userName"
              placeholder="Email or mobile"
              value={otpFormik.values.userName}
              onChange={otpFormik.handleChange}
              onBlur={otpFormik.handleBlur}
              error={
                (formSubmitted || otpFormik.submitCount > 0)
                && otpFormik.touched.userName
                && otpFormik.errors.userName
              }
            />
          </div>
          <Button type="submit" appearance="auth-btn" className="auth-primary-btn">
            Send OTP
          </Button>
        </form>
      );
    }

    return (
      <form onSubmit={otpFormik.handleSubmit} className="auth-form">
        <p className="auth-otp-sent">
          One-Time Password sent to <strong>{otpFormik.values.userName}</strong>
          <button type="button" className="auth-otp-change" onClick={resetOtpFlow}>
            Change
          </button>
        </p>
        <OtpInputs
          value={otpFormik.values.verificationCode}
          onChange={(code) => otpFormik.setFieldValue("verificationCode", code)}
        />
        <Button type="submit" appearance="auth-btn" className="auth-primary-btn">
          Sign in
        </Button>
      </form>
    );
  }

  return (
    <div className="auth-page signin-page">
      <div className="auth-shell">
        {/* <aside className="auth-side">
          <span className="auth-side-tag">ORKAAPS CONSOLE</span>
          <h2 className="auth-side-title">Operate WhatsApp at scale.</h2>
          <p className="auth-side-text">
            Secure access for campaigns, contacts, team workflows, and customer
            conversations.
          </p>
          <ul className="auth-side-list">
            <li><span className="auth-dot" /> Real-time chat visibility</li>
            <li><span className="auth-dot" /> Broadcast and automation tools</li>
            <li><span className="auth-dot" /> Protected account access</li>
          </ul>
        </aside> */}
        <div className="auth-card">
          <Logo />
          <h1 className="auth-title">Sign in</h1>
          <p className="auth-subtitle">
            Sign in with your email or mobile and password.
          </p>

          <SignInTabs loginMode={loginMode} onModeChange={handleModeChange} />

          {loginMode === LOGIN_MODE.Password ? renderPasswordForm() : renderOtpForm()}

          {loginMode === LOGIN_MODE.Password && (
            <OAuthProviderButtons
              onError={showError}
              onAuthenticated={async (body) => {
                persistAuthTokenFromResponse(body);
                await redirectAfterLogin();
              }}
            />
          )}

          
          <p className="auth-terms">
            By continuing, you agree to our{" "}
            <a
              className="ui-link auth-terms-link"
              href="https://orkaapps.com/links/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="ui-link auth-terms-link"
              href="https://orkaapps.com/links/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>


          <div className="auth-footer">
            New to WappGrow?
            <a href="/signup" className="ui-link auth-footer-link">
              Create account
            </a>
          </div>
          {snackBar()}
        </div>
      </div>
    </div>
  );
}

export { SignInPage };
